/**
 * EvidencePreviewDialog - aperçu d'une preuve de contrôle en popup (modale).
 * Vue structurée (clés/valeurs, tables pour les tableaux d'objets) + JSON brut,
 * avec copie et téléchargement. Partagé par les pages de détail M365 et Azure.
 */
import {
  Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions,
  Button, Caption1, tokens,
} from '@fluentui/react-components';
import { Copy20Regular, ArrowDownload20Regular } from '@fluentui/react-icons';

function isObj(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

/** Rendu récursif (borné) d'une valeur de preuve. */
export function EvidenceValueView({ value, depth = 0 }: { value: unknown; depth?: number }) {
  if (value === null || value === undefined || value === '') {
    return <em style={{ color: tokens.colorNeutralForeground3 }}>—</em>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <em style={{ color: tokens.colorNeutralForeground3 }}>—</em>;
    // Tableau d'objets -> table
    if (value.every(isObj) && depth < 3) {
      const rows = value.slice(0, 100) as Record<string, unknown>[];
      const cols = Array.from(new Set(rows.flatMap((o) => Object.keys(o)))).slice(0, 12);
      return (
        <div style={{ overflow: 'auto', maxHeight: 300, border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: 6, marginTop: 4 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: tokens.colorNeutralBackground2 }}>
                {cols.map((c) => <th key={c} style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600 }}>{c}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} style={{ borderTop: `1px solid ${tokens.colorNeutralStroke3}` }}>
                  {cols.map((c) => (
                    <td key={c} style={{ padding: '5px 10px', fontFamily: 'Consolas, monospace' }}>
                      {typeof r[c] === 'object' ? JSON.stringify(r[c]) : String(r[c] ?? '')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    // Tableau de scalaires
    return <span>{value.map((v) => (typeof v === 'object' ? JSON.stringify(v) : String(v))).join(', ')}</span>;
  }
  if (isObj(value)) {
    if (depth >= 2) {
      return <code style={{ fontFamily: 'Consolas, monospace', fontSize: 12 }}>{JSON.stringify(value)}</code>;
    }
    return (
      <div style={{ marginLeft: depth ? 12 : 0 }}>
        {Object.entries(value).map(([k, v]) => (
          <div key={k} style={{ marginBottom: 3 }}>
            <span style={{ fontWeight: 600, color: tokens.colorNeutralForeground2 }}>{k}</span>
            {' : '}
            <EvidenceValueView value={v} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }
  return <span>{String(value)}</span>;
}

export function EvidencePreviewDialog({
  open, onOpenChange, title, evidence, fileName = 'evidence.json',
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  evidence: unknown;
  fileName?: string;
}) {
  const json = JSON.stringify(evidence, null, 2);

  function copy() { void navigator.clipboard?.writeText(json); }
  function download() {
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = fileName; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <Dialog open={open} onOpenChange={(_, d) => onOpenChange(d.open)}>
      <DialogSurface style={{ maxWidth: 920 }}>
        <DialogBody>
          <DialogTitle>{title}</DialogTitle>
          <DialogContent>
            <Caption1 style={{ display: 'block', color: tokens.colorNeutralForeground3, marginBottom: 8, textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.5, fontWeight: 700 }}>
              Vue structurée
            </Caption1>
            <div style={{ fontSize: 13, lineHeight: 1.6, marginBottom: 16 }}>
              <EvidenceValueView value={evidence} />
            </div>
            <Caption1 style={{ display: 'block', color: tokens.colorNeutralForeground3, marginBottom: 6, textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.5, fontWeight: 700 }}>
              JSON brut
            </Caption1>
            <pre style={{
              margin: 0, padding: 12, background: '#0F2638', color: '#D7E3EE',
              borderRadius: 6, fontSize: 11, fontFamily: 'Consolas, monospace',
              overflow: 'auto', maxHeight: 340,
            }}>
              {json}
            </pre>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" icon={<Copy20Regular />} onClick={copy}>Copier le JSON</Button>
            <Button appearance="secondary" icon={<ArrowDownload20Regular />} onClick={download}>Télécharger</Button>
            <Button appearance="primary" onClick={() => onOpenChange(false)}>Fermer</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
